import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';

import { Card } from '@repo/ui/Card';
import { Form } from '@repo/ui/Form';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';

import { TextInputField, TextAreaField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';

const updateSubAccountFormSchema = z.object({
  name: z.string().min(2, { message: 'Name must contain at least 2 characters.' }),
  notes: z.string().optional(),
});

type UpdateSubAccountFormParams = TypeOf<typeof updateSubAccountFormSchema>;

export interface UpdateSubAccountProps {
  subAccountId: string;
  name: string;
  notes?: string;
  lrn_access?: boolean;
  sms_access?: boolean;
  mms_access?: boolean;
  require_ip_api_access?: boolean;
  require_ip_gql_access?: boolean;
  refetch: () => void;
}

const UpdateSubAccountForm = ({ subAccountId, name, notes, refetch }: UpdateSubAccountProps) => {
  // Mutation
  const UPDATE_SUB_ACCOUNT = gql`
    mutation UpdateSubAccount($id: ID!, $input: UpdateAccountInput!) {
      updateAccount(id: $id, input: $input) {
        account {
          id
          name
          notes
        }
      }
    }
  `;

  const client = useApolloClient();
  const onSubmit = async ({ ...input }: UpdateSubAccountFormParams) => {
    await client.mutate({
      mutation: UPDATE_SUB_ACCOUNT,
      variables: {
        input,
        id: subAccountId,
      },
    });

    refetch();
  };

  const form = useZodForm({
    schema: updateSubAccountFormSchema,
    onSubmit,
  });

  return (
    <>
      <Card>
        <Form {...form}>
          <Form.Success>Sub account successfully updated.</Form.Success>
          <Form.Error>
            Unable to update sub account. Please try again later or contact support if error
            persists.
          </Form.Error>
          <TextInputField label="Name" defaultValue={name} {...registerInput(form, 'name')} />
          <TextAreaField
            label="Description"
            defaultValue={notes}
            {...registerInput(form, 'notes')}
          />

          <div className="w-full">
            <Button type="submit">Update Sub Account</Button>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default UpdateSubAccountForm;
